<template>
  <div class="monitoring-page cell auto cell-block-y">
    <div class="monitoring-wrap">
      <div class="monitoring-header">
        <div>
          <div class="monitoring-title-row">
            <h1 class="monitoring-title">iWALY System Health</h1>
            <span :class="['status-badge', `status-${dashboardStatus}`]">
              <span class="status-dot"></span>
              {{ statusLabel(dashboardStatus) }}
            </span>
          </div>
          <p class="monitoring-subtitle">
            {{ environmentLabel }} · Docker · Laravel · MySQL · nginx · резервные копии · AI-диагностика
          </p>
        </div>
        <div class="monitoring-header-actions">
          <div class="updated-at">
            <span v-if="loading">Обновление…</span>
            <span v-else-if="generatedAt">Обновлено {{ formatRelative(generatedAt) }}</span>
            <span v-else>Данных пока нет</span>
          </div>
          <button
            class="monitor-btn monitor-btn-secondary"
            :disabled="testingNotification"
            @click="sendTestNotification"
          >
            {{ testingNotification ? 'Отправляю…' : (testNotificationSent ? 'Тест отправлен' : 'Тест TG + MAX') }}
          </button>
          <button class="monitor-btn monitor-btn-secondary" :disabled="loading" @click="fetchDashboard">
            Обновить
          </button>
        </div>
      </div>

      <div v-if="stale" class="stale-banner">
        <strong>Нет свежих данных мониторинга.</strong>
        Последний снимок старше {{ staleAfterMinutes }} минут. Это считается критическим состоянием,
        потому что сам watcher может быть недоступен.
      </div>

      <div class="range-row">
        <button
          v-for="item in ranges"
          :key="item.value"
          :class="['range-btn', { active: range === item.value }]"
          @click="setRange(item.value)"
        >
          {{ item.label }}
        </button>
      </div>

      <div class="panel ai-settings-panel">
        <div class="panel-heading ai-settings-heading">
          <div>
            <h2>AI Agent</h2>
            <p>Groq / REG.Cloud / OpenRouter · deterministic monitoring работает независимо от AI</p>
          </div>
          <span v-if="aiSettings" :class="['status-badge', aiSettings.configured ? 'status-ok' : 'status-unknown']">
            {{ aiSettings.configured ? 'ADMIN CONFIG' : 'HOST CONFIG' }}
          </span>
        </div>

        <div v-if="aiSettingsLoading" class="ai-settings-loading">Загрузка настроек…</div>
        <div v-else-if="aiSettings" class="ai-settings-body">
          <div v-if="!aiSettings.configured" class="ai-settings-preset-note">
            Пока действуют локальные host-настройки. Ниже показан рекомендуемый пресет; он станет активным только после нажатия «Сохранить AI-настройки».
          </div>
          <div class="ai-settings-main-grid">
            <label class="setting-toggle setting-toggle-master">
              <input v-model="aiSettings.enabled" type="checkbox">
              <span>AI-анализ включён</span>
            </label>
            <label class="setting-toggle">
              <input v-model="aiSettings.analyzeCritical" type="checkbox" :disabled="!aiSettings.enabled">
              <span>Critical</span>
            </label>
            <label class="setting-toggle">
              <input v-model="aiSettings.analyzeWarning" type="checkbox" :disabled="!aiSettings.enabled">
              <span>Warning</span>
            </label>
            <label class="setting-toggle">
              <input v-model="aiSettings.freeOnly" type="checkbox" :disabled="!aiSettings.enabled">
              <span>Только бесплатные</span>
            </label>
            <label class="ai-limit-field">
              <span>Лимит AI-разборов / сутки</span>
              <input v-model.number="aiSettings.dailyAnalysisLimit" type="number" min="1" max="1000" :disabled="!aiSettings.enabled">
            </label>
          </div>

          <div class="provider-table">
            <div class="provider-table-head">
              <span>ON</span><span>Провайдер</span><span>Model ID</span><span>Режим</span>
            </div>
            <div v-for="(slot, index) in aiSettings.providers" :key="index" class="provider-row">
              <input v-model="slot.enabled" type="checkbox" :disabled="!aiSettings.enabled">
              <select v-model="slot.provider" :disabled="!aiSettings.enabled">
                <option value="groq">Groq</option>
                <option value="regcloud">REG.Cloud</option>
                <option value="openrouter">OpenRouter</option>
              </select>
              <input v-model.trim="slot.model" type="text" maxlength="160" :disabled="!aiSettings.enabled" placeholder="Model ID">
              <span :class="['provider-mode', providerIsFree(slot.provider) ? 'free' : 'paid']">
                {{ providerIsFree(slot.provider) ? 'FREE' : 'PAID' }}
              </span>
            </div>
          </div>

          <div class="ai-settings-note">
            API-ключи здесь не хранятся и в браузер не передаются. Они остаются в root-only secrets-файле host-monitor.
            При «Только бесплатные» платный REG.Cloud автоматически пропускается.
          </div>

          <div class="ai-settings-actions">
            <button class="monitor-btn monitor-btn-primary" :disabled="aiSettingsSaving" @click="saveAiSettings">
              {{ aiSettingsSaving ? 'Сохраняю…' : (aiSettingsSaved ? 'Сохранено' : 'Сохранить AI-настройки') }}
            </button>
            <span class="ai-usage-hint">{{ aiUsageHint }}</span>
          </div>

          <div class="diagnostic-export">
            <div>
              <strong>Диагностический пакет для глубокого AI-анализа</strong>
              <span>Snapshots, incidents, events, AI-разборы и ограниченные фрагменты логов.</span>
            </div>
            <div class="diagnostic-actions">
              <button v-for="days in [1, 7, 30]" :key="days" class="monitor-btn monitor-btn-secondary" :disabled="exportingDays !== null" @click="downloadDiagnostic(days)">
                {{ exportingDays === days ? 'Готовлю…' : ('Скачать ' + days + ' дн.') }}
              </button>
              <button class="monitor-btn monitor-btn-secondary" :disabled="!aiSettings.analysisPrompt" @click="copyAnalysisPrompt">
                {{ promptCopied ? 'Промпт скопирован' : 'Скопировать промпт для ИИ' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="!latest && !loading" class="empty-state">
        <div class="empty-state-title">Мониторинг ещё не прислал ни одного снимка</div>
        <div class="empty-state-text">
          После установки host watcher данные появятся здесь автоматически. До этого экран специально
          не подменяет отсутствие контроля зелёным статусом.
        </div>
      </div>

      <template v-if="latest">
        <div class="health-grid">
          <HealthCard
            title="API"
            :status="apiStatus"
            :value="apiValue"
            :detail="apiDetail"
          />
          <HealthCard
            title="Backend"
            :status="serviceStatus(latest.appContainerStatus)"
            :value="serviceValue(latest.appContainerStatus)"
            :detail="metric('appContainerName') || 'app'"
          />
          <HealthCard
            title="MySQL"
            :status="serviceStatus(latest.dbContainerStatus)"
            :value="serviceValue(latest.dbContainerStatus)"
            :detail="metric('dbContainerName') || 'mysql'"
          />
          <HealthCard
            title="nginx"
            :status="serviceStatus(latest.nginxStatus)"
            :value="serviceValue(latest.nginxStatus)"
            detail="reverse proxy"
          />
          <HealthCard
            title="MySQL backup"
            :status="backupStatus(latest.backupStatus)"
            :value="backupValue(latest.backupStatus)"
            :detail="backupDetail(latest.backupStatus, latest.backupAgeMinutes)"
          />
          <HealthCard
            title="Storage backup"
            :status="backupStatus(metric('storageBackupStatus'))"
            :value="backupValue(metric('storageBackupStatus'))"
            :detail="backupDetail(metric('storageBackupStatus'), metric('storageBackupAgeMinutes'))"
          />
          <HealthCard
            title="Storage archive"
            :status="backupStatus(metric('storageArchiveStatus'))"
            :value="backupValue(metric('storageArchiveStatus'))"
            :detail="backupDetail(metric('storageArchiveStatus'), metric('storageArchiveAgeMinutes'))"
          />
          <HealthCard
            title="AI-диагностика"
            :status="aiHealthStatus"
            :value="aiHealthValue"
            :detail="aiHealthDetail"
          />
          <HealthCard
            title="Оповещения"
            :status="notificationHealthStatus"
            :value="notificationHealthValue"
            detail="административные каналы"
          />
          <HealthCard
            title="SMS Aero"
            :status="smsProviderHealth('smsaero')"
            :value="smsProviderBalance('smsaero')"
            :detail="smsProviderDetail('smsaero')"
          />
          <HealthCard
            title="SMSimple"
            :status="smsProviderHealth('smsimple')"
            :value="smsProviderBalance('smsimple')"
            :detail="smsProviderDetail('smsimple')"
          />
          <HealthCard
            title="Health checks"
            :status="healthPercentStatus"
            :value="healthyPercentLabel"
            :detail="checksLabel"
          />
        </div>

        <div class="resource-grid">
          <MetricCard title="CPU" :value="latest.cpuPercent" unit="%" :warning="85" :critical="95" />
          <MetricCard title="RAM" :value="latest.memoryPercent" unit="%" :warning="85" :critical="95" />
          <MetricCard title="Disk /var" :value="latest.diskPercent" unit="%" :warning="80" :critical="92" />
          <MetricCard title="Inodes /var" :value="metric('inodePercent')" unit="%" :warning="85" :critical="95" />
        </div>

        <div class="charts-grid">
          <div class="panel chart-panel">
            <div class="panel-heading">
              <div>
                <h2>Ресурсы</h2>
                <p>CPU, RAM и заполнение диска за выбранный период</p>
              </div>
            </div>
            <div class="chart-row" v-for="series in resourceSeries" :key="series.key">
              <div class="chart-label">
                <span>{{ series.label }}</span>
                <strong>{{ series.current }}</strong>
              </div>
              <svg class="sparkline" viewBox="0 0 360 56" preserveAspectRatio="none" aria-hidden="true">
                <line x1="0" y1="55" x2="360" y2="55" class="chart-base" />
                <polyline :points="sparklinePoints(series.key, 100)" class="chart-line" />
              </svg>
            </div>
          </div>

          <div class="panel chart-panel">
            <div class="panel-heading">
              <div>
                <h2>API response</h2>
                <p>Время ответа health endpoint</p>
              </div>
            </div>
            <div class="api-chart-value">{{ apiValue }}</div>
            <svg class="sparkline sparkline-large" viewBox="0 0 360 110" preserveAspectRatio="none" aria-hidden="true">
              <line x1="0" y1="109" x2="360" y2="109" class="chart-base" />
              <polyline :points="sparklinePoints('apiResponseMs', apiMax)" class="chart-line" />
            </svg>
            <div class="chart-footnote">Предупреждение: от 2000 ms</div>
          </div>
        </div>
      </template>

      <div class="panel incidents-panel">
        <div class="panel-heading incident-heading">
          <div>
            <h2>Открытые инциденты</h2>
            <p>Требуют внимания или уже отмечены администратором</p>
          </div>
          <div class="incident-counters">
            <span class="counter critical">{{ stat('openCritical', 0) }} critical</span>
            <span class="counter warning">{{ stat('openWarning', 0) }} warning</span>
          </div>
        </div>

        <div v-if="openIncidents.length === 0" class="all-clear">
          <span class="all-clear-mark">✓</span>
          <div>
            <strong>Открытых инцидентов нет</strong>
            <p>Watcher не сообщает о текущих проблемах.</p>
          </div>
        </div>

        <div v-else class="incident-list">
          <button
            v-for="item in openIncidents"
            :key="item.id"
            class="incident-row"
            @click="openIncident(item.id)"
          >
            <span :class="['incident-severity', item.severity]">{{ severityIcon(item.severity) }}</span>
            <span class="incident-main">
              <span class="incident-title">{{ item.title }}</span>
              <span class="incident-meta">
                {{ componentLabel(item.component) }} · {{ formatDate(item.lastSeenAt) }} ·
                повторений {{ item.occurrenceCount || 1 }}
              </span>
              <span v-if="item.aiSummary" class="incident-ai">AI: {{ truncate(item.aiSummary, 180) }}</span>
            </span>
            <span :class="['incident-state', item.status]">{{ incidentStatusLabel(item.status) }}</span>
            <span class="incident-arrow">›</span>
          </button>
        </div>
      </div>

      <div class="two-column-grid">
        <div class="panel">
          <div class="panel-heading">
            <div>
              <h2>Последние события</h2>
              <p>Техническая хронология мониторинга</p>
            </div>
          </div>
          <div v-if="events.length === 0" class="panel-empty">Событий пока нет.</div>
          <div v-else class="events-list">
            <div v-for="item in events.slice(0, 18)" :key="item.id" class="event-row">
              <span :class="['event-dot', item.level]"></span>
              <span class="event-content">
                <strong>{{ item.message }}</strong>
                <span>{{ componentLabel(item.component) }} · {{ formatDate(item.occurredAt) }}</span>
              </span>
            </div>
          </div>
        </div>

        <div class="panel">
          <div class="panel-heading">
            <div>
              <h2>История инцидентов</h2>
              <p>Последние обнаруженные проблемы, включая решённые</p>
            </div>
          </div>
          <div v-if="recentIncidents.length === 0" class="panel-empty">Истории пока нет.</div>
          <div v-else class="history-list">
            <button
              v-for="item in recentIncidents.slice(0, 18)"
              :key="item.id"
              class="history-row"
              @click="openIncident(item.id)"
            >
              <span :class="['event-dot', item.severity]"></span>
              <span class="history-main">
                <strong>{{ item.title }}</strong>
                <span>{{ formatDate(item.detectedAt) }}</span>
              </span>
              <span :class="['incident-state', item.status]">{{ incidentStatusLabel(item.status) }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="incidentModalOpen" class="incident-modal-backdrop" @click.self="closeIncident">
      <div class="incident-modal">
        <div class="incident-modal-header">
          <div v-if="incidentLoading" class="modal-loading">Загрузка инцидента…</div>
          <template v-else-if="selectedIncident">
            <div>
              <div class="modal-kicker">
                Incident #{{ selectedIncident.id }} · {{ componentLabel(selectedIncident.component) }}
              </div>
              <h2>{{ selectedIncident.title }}</h2>
            </div>
            <button class="modal-close" aria-label="Закрыть" @click="closeIncident">×</button>
          </template>
          <button v-else class="modal-close" aria-label="Закрыть" @click="closeIncident">×</button>
        </div>

        <div v-if="selectedIncident && !incidentLoading" class="incident-modal-body">
          <div class="incident-summary-strip">
            <span :class="['status-badge', `status-${selectedIncident.severity}`]">
              {{ severityLabel(selectedIncident.severity) }}
            </span>
            <span :class="['incident-state', selectedIncident.status]">
              {{ incidentStatusLabel(selectedIncident.status) }}
            </span>
            <span>Обнаружен {{ formatDate(selectedIncident.detectedAt) }}</span>
            <span>Последний раз {{ formatDate(selectedIncident.lastSeenAt) }}</span>
            <span>Повторений: {{ selectedIncident.occurrenceCount || 1 }}</span>
          </div>

          <section v-if="selectedIncident.aiSummary" class="detail-section ai-section">
            <div class="detail-section-title">AI-анализ</div>
            <p class="detail-text">{{ selectedIncident.aiSummary }}</p>
            <div class="ai-confidence">
              <span v-if="selectedIncident.payload && selectedIncident.payload.aiProvider">Провайдер: {{ providerLabel(selectedIncident.payload.aiProvider) }}</span>
              <span v-if="selectedIncident.aiModel"> · Модель: {{ selectedIncident.aiModel }}</span>
              <span v-if="aiConfidence"> · Уверенность: {{ confidenceLabel(aiConfidence) }}</span>
            </div>
          </section>

          <section v-if="selectedIncident.aiRecommendations" class="detail-section">
            <div class="detail-section-title">Рекомендации</div>
            <pre class="recommendations">{{ selectedIncident.aiRecommendations }}</pre>
          </section>

          <section v-if="selectedIncident.summary" class="detail-section">
            <div class="detail-section-title">Кратко</div>
            <p class="detail-text">{{ selectedIncident.summary }}</p>
          </section>

          <section v-if="selectedIncident.technicalDetails" class="detail-section">
            <div class="detail-section-title">Технические детали</div>
            <pre class="technical-text">{{ selectedIncident.technicalDetails }}</pre>
          </section>

          <section v-if="selectedIncident.logExcerpt" class="detail-section">
            <div class="detail-section-title">Фрагмент логов</div>
            <pre class="logs-box">{{ selectedIncident.logExcerpt }}</pre>
          </section>

          <section v-if="incidentEvents.length" class="detail-section">
            <div class="detail-section-title">Хронология инцидента</div>
            <div class="events-list incident-events">
              <div v-for="item in incidentEvents" :key="item.id" class="event-row">
                <span :class="['event-dot', item.level]"></span>
                <span class="event-content">
                  <strong>{{ item.message }}</strong>
                  <span>{{ formatDate(item.occurredAt) }}</span>
                </span>
              </div>
            </div>
          </section>
        </div>

        <div v-if="selectedIncident && !incidentLoading" class="incident-modal-footer">
          <div class="footer-note">Изменения на сервере автоматически не выполняются.</div>
          <button
            v-if="selectedIncident.status === 'open'"
            class="monitor-btn monitor-btn-primary"
            :disabled="acknowledging"
            @click="acknowledgeIncident"
          >
            {{ acknowledging ? 'Сохраняю…' : 'Отметить как просмотренный' }}
          </button>
          <button class="monitor-btn monitor-btn-secondary" @click="closeIncident">Закрыть</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import {
  acknowledgeMonitoringIncident,
  monitoringAiSettings,
  monitoringDashboard,
  monitoringDiagnosticExport,
  monitoringIncident,
  testMonitoringNotification,
  updateMonitoringAiSettings,
} from '@/api/settings';

const HealthCard = {
  props: {
    title: { type: String, required: true },
    status: { type: String, default: 'unknown' },
    value: { type: String, default: '—' },
    detail: { type: String, default: '' },
  },
  template: `
    <div class="health-card">
      <div class="health-card-head">
        <span class="health-card-title">{{ title }}</span>
        <span :class="['health-indicator', 'indicator-' + status]"></span>
      </div>
      <div class="health-card-value">{{ value }}</div>
      <div class="health-card-detail">{{ detail || '—' }}</div>
    </div>
  `,
};

const MetricCard = {
  props: {
    title: { type: String, required: true },
    value: { type: [Number, String], default: null },
    unit: { type: String, default: '' },
    warning: { type: Number, default: 80 },
    critical: { type: Number, default: 95 },
  },
  computed: {
    numericValue() {
      const parsed = Number(this.value);
      return Number.isNaN(parsed) ? null : parsed;
    },
    label() {
      return this.numericValue === null ? '—' : `${Math.round(this.numericValue * 10) / 10}${this.unit}`;
    },
    level() {
      if (this.numericValue === null) return 'unknown';
      if (this.numericValue >= this.critical) return 'critical';
      if (this.numericValue >= this.warning) return 'warning';
      return 'ok';
    },
    width() {
      if (this.numericValue === null) return '0%';
      return `${Math.max(0, Math.min(100, this.numericValue))}%`;
    },
  },
  template: `
    <div class="metric-card">
      <div class="metric-card-top">
        <span>{{ title }}</span>
        <strong :class="['metric-value', 'metric-' + level]">{{ label }}</strong>
      </div>
      <div class="metric-track">
        <div :class="['metric-fill', 'metric-fill-' + level]" :style="{ width: width }"></div>
      </div>
    </div>
  `,
};

export default {
  components: {
    HealthCard,
    MetricCard,
  },
  data() {
    return {
      loading: false,
      range: '24h',
      dashboard: null,
      timer: null,
      incidentModalOpen: false,
      incidentLoading: false,
      acknowledging: false,
      testingNotification: false,
      testNotificationSent: false,
      selectedIncident: null,
      aiSettings: null,
      aiSettingsLoading: false,
      aiSettingsSaving: false,
      aiSettingsSaved: false,
      exportingDays: null,
      promptCopied: false,
      ranges: [
        { value: '1h', label: '1 час' },
        { value: '24h', label: '24 часа' },
        { value: '7d', label: '7 дней' },
        { value: '30d', label: '30 дней' },
      ],
    };
  },
  computed: {
    dashboardStatus() {
      return this.dashboard && this.dashboard.status ? this.dashboard.status : 'unknown';
    },
    environmentLabel() {
      const value = this.dashboard && this.dashboard.environment
        ? String(this.dashboard.environment).toUpperCase() : 'ENV';
      return value === 'MASTER' ? 'PRODUCTION' : value;
    },
    stale() {
      return Boolean(this.dashboard && this.dashboard.stale);
    },
    staleAfterMinutes() {
      return this.dashboard && this.dashboard.staleAfterMinutes ? this.dashboard.staleAfterMinutes : 7;
    },
    latest() {
      return this.dashboard ? this.dashboard.latest : null;
    },
    generatedAt() {
      return this.dashboard ? this.dashboard.generatedAt : null;
    },
    metrics() {
      return this.dashboard && Array.isArray(this.dashboard.metrics) ? this.dashboard.metrics : [];
    },
    openIncidents() {
      return this.dashboard && Array.isArray(this.dashboard.openIncidents) ? this.dashboard.openIncidents : [];
    },
    recentIncidents() {
      return this.dashboard && Array.isArray(this.dashboard.recentIncidents) ? this.dashboard.recentIncidents : [];
    },
    events() {
      return this.dashboard && Array.isArray(this.dashboard.events) ? this.dashboard.events : [];
    },
    smsProviders() {
      return this.dashboard && Array.isArray(this.dashboard.smsProviders)
        ? this.dashboard.smsProviders : [];
    },
    incidentEvents() {
      return this.selectedIncident && Array.isArray(this.selectedIncident.events)
        ? this.selectedIncident.events : [];
    },
    aiConfidence() {
      if (!this.selectedIncident || !this.selectedIncident.payload) return '';
      return this.selectedIncident.payload.aiConfidence || '';
    },
    apiStatus() {
      if (!this.latest) return 'unknown';
      const code = Number(this.metric('apiHttpCode'));
      const response = Number(this.latest.apiResponseMs);
      if (code !== 200) return 'critical';
      if (!Number.isNaN(response) && response >= 2000) return 'warning';
      return 'ok';
    },
    apiValue() {
      if (!this.latest || this.latest.apiResponseMs === null || this.latest.apiResponseMs === undefined) return '—';
      return `${this.latest.apiResponseMs} ms`;
    },
    apiDetail() {
      const code = this.metric('apiHttpCode');
      return code ? `HTTP ${code}` : 'health endpoint';
    },
    aiHealthStatus() {
      const state = String(this.metric('aiState') || '').toLowerCase();
      const enabled = this.metric('aiEnabled');

      if (state === 'ready') return 'ok';
      if (enabled === false || state === 'disabled') return 'ok';
      if (state === 'cooldown' || state === 'rate_limit' || state === 'source_error' || state === 'daily_limit') return 'warning';
      if (state === 'missing_key' || state === 'agent_missing' || state === 'no_provider') return 'warning';

      // Backward compatibility with snapshots created before generic AI metrics.
      if (this.metric('claudeAuthenticated')) return 'ok';
      if (this.metric('claudeAvailable')) return 'warning';
      return 'unknown';
    },
    aiHealthValue() {
      const state = String(this.metric('aiState') || '').toLowerCase();
      const labels = {
        ready: 'READY',
        cooldown: 'COOLDOWN',
        rate_limit: 'RATE LIMIT',
        source_error: 'SOURCE ERROR',
        missing_key: 'NO KEY',
        agent_missing: 'OFFLINE',
        no_provider: 'NO PROVIDER',
        daily_limit: 'DAILY LIMIT',
        disabled: 'DISABLED',
      };

      if (labels[state]) return labels[state];
      if (this.metric('claudeAuthenticated')) return 'READY';
      if (this.metric('claudeAvailable')) return 'AVAILABLE';
      return 'UNKNOWN';
    },
    aiHealthDetail() {
      const provider = String(this.metric('aiProvider') || '').toLowerCase();
      const model = this.metric('aiModel') || '';
      const source = this.metric('aiConfigSource') === 'admin' ? 'admin' : 'host';
      if (!provider && String(this.metric('aiState') || '').toLowerCase() === 'disabled') return `выключено · ${source}`;
      return `${this.providerLabel(provider || 'regcloud')} · ${model || 'model'} · ${source}`;
    },
    aiUsageHint() {
      const used = Number(this.metric('aiDailyAnalysisCount'));
      const configuredLimit = this.aiSettings ? Number(this.aiSettings.dailyAnalysisLimit) : NaN;
      const snapshotLimit = Number(this.metric('aiDailyAnalysisLimit'));
      const limit = !Number.isNaN(snapshotLimit) && snapshotLimit > 0 ? snapshotLimit : configuredLimit;
      if (Number.isNaN(used) || Number.isNaN(limit)) return 'Суточный расход появится после следующего цикла monitor.';
      return `Сегодня: ${used} / ${limit} AI-разборов`;
    },
    notificationHealthStatus() {
      return this.metric('telegramConfigured') && this.metric('maxConfigured') ? 'ok' : 'warning';
    },
    notificationHealthValue() {
      const telegram = this.metric('telegramConfigured') ? 'TG ✓' : 'TG —';
      const max = this.metric('maxConfigured') ? 'MAX ✓' : 'MAX —';
      return `${telegram} · ${max}`;
    },
    healthPercentStatus() {
      const value = this.dashboard && this.dashboard.stats ? Number(this.dashboard.stats.healthyPercent) : NaN;
      if (Number.isNaN(value)) return 'unknown';
      if (value < 95) return 'critical';
      if (value < 99) return 'warning';
      return 'ok';
    },
    healthyPercentLabel() {
      const value = this.dashboard && this.dashboard.stats ? this.dashboard.stats.healthyPercent : null;
      return value === null || value === undefined ? '—' : `${value}%`;
    },
    checksLabel() {
      const value = this.dashboard && this.dashboard.stats ? this.dashboard.stats.checks : 0;
      return `${value} проверок`;
    },
    resourceSeries() {
      return [
        { key: 'cpu', label: 'CPU', current: this.metricLabel(this.latest && this.latest.cpuPercent, '%') },
        { key: 'memory', label: 'RAM', current: this.metricLabel(this.latest && this.latest.memoryPercent, '%') },
        { key: 'disk', label: 'Disk', current: this.metricLabel(this.latest && this.latest.diskPercent, '%') },
      ];
    },
    apiMax() {
      const values = this.metrics
        .map((item) => Number(item.apiResponseMs))
        .filter((value) => !Number.isNaN(value));
      const max = values.length ? Math.max.apply(null, values) : 2000;
      return Math.max(2000, max * 1.1);
    },
  },
  watch: {
    '$route.query.incident': {
      immediate: true,
      handler(value) {
        if (value) this.openIncident(value, false);
      },
    },
  },
  created() {
    this.fetchDashboard();
    this.fetchAiSettings();
    this.timer = window.setInterval(() => this.fetchDashboard(true), 30000);
  },
  beforeDestroy() {
    if (this.timer) window.clearInterval(this.timer);
  },
  methods: {
    async fetchAiSettings() {
      this.aiSettingsLoading = true;
      try {
        const response = await monitoringAiSettings();
        this.aiSettings = JSON.parse(JSON.stringify(response.data));
      } finally {
        this.aiSettingsLoading = false;
      }
    },
    async saveAiSettings() {
      if (!this.aiSettings || this.aiSettingsSaving) return;
      this.aiSettingsSaving = true;
      this.aiSettingsSaved = false;
      try {
        const payload = {
          enabled: Boolean(this.aiSettings.enabled),
          analyzeWarning: Boolean(this.aiSettings.analyzeWarning),
          analyzeCritical: Boolean(this.aiSettings.analyzeCritical),
          freeOnly: Boolean(this.aiSettings.freeOnly),
          dailyAnalysisLimit: Number(this.aiSettings.dailyAnalysisLimit),
          providers: this.aiSettings.providers.map((item) => ({
            enabled: Boolean(item.enabled),
            provider: item.provider,
            model: item.model,
          })),
        };
        const response = await updateMonitoringAiSettings(payload);
        this.aiSettings = JSON.parse(JSON.stringify(response.data));
        this.aiSettingsSaved = true;
        window.setTimeout(() => { this.aiSettingsSaved = false; }, 3500);
      } finally {
        this.aiSettingsSaving = false;
      }
    },
    providerLabel(provider) {
      const labels = { groq: 'Groq', regcloud: 'REG.Cloud', openrouter: 'OpenRouter' };
      return labels[String(provider || '').toLowerCase()] || String(provider || 'AI');
    },
    providerIsFree(provider) {
      const meta = this.aiSettings && this.aiSettings.providersMeta
        ? this.aiSettings.providersMeta[String(provider || '').toLowerCase()] : null;
      if (meta && Object.prototype.hasOwnProperty.call(meta, 'free')) return Boolean(meta.free);
      return ['groq', 'openrouter'].includes(String(provider || '').toLowerCase());
    },
    async downloadDiagnostic(days) {
      if (this.exportingDays !== null) return;
      this.exportingDays = days;
      try {
        const response = await monitoringDiagnosticExport(days);
        const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `iwaly-monitoring-${days}d-${moment().format('YYYYMMDD-HHmmss')}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } finally {
        this.exportingDays = null;
      }
    },
    async copyAnalysisPrompt() {
      const text = this.aiSettings && this.aiSettings.analysisPrompt ? this.aiSettings.analysisPrompt : '';
      if (!text) return;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const area = document.createElement('textarea');
        area.value = text;
        area.style.position = 'fixed';
        area.style.opacity = '0';
        document.body.appendChild(area);
        area.select();
        document.execCommand('copy');
        document.body.removeChild(area);
      }
      this.promptCopied = true;
      window.setTimeout(() => { this.promptCopied = false; }, 3500);
    },
    async fetchDashboard(silent = false) {
      if (this.loading && silent) return;
      if (!silent) this.loading = true;
      try {
        const response = await monitoringDashboard(this.range);
        this.dashboard = response.data;
      } finally {
        if (!silent) this.loading = false;
      }
    },
    async setRange(value) {
      if (this.range === value) return;
      this.range = value;
      await this.fetchDashboard();
    },
    metric(key) {
      if (!this.latest || !this.latest.metrics) return null;
      return this.latest.metrics[key];
    },
    stat(key, fallback) {
      if (!this.dashboard || !this.dashboard.stats) return fallback;
      const value = this.dashboard.stats[key];
      return value === null || value === undefined ? fallback : value;
    },
    smsProvider(key) {
      return this.smsProviders.find((item) => item.key === key) || null;
    },
    smsProviderHealth(key) {
      const item = this.smsProvider(key);
      if (!item) return 'unknown';
      if (item.status === 'ok') return 'ok';
      if (item.status === 'not_configured') return 'warning';
      return 'critical';
    },
    smsProviderBalance(key) {
      const item = this.smsProvider(key);
      if (!item || item.balance === null || item.balance === undefined) return '—';
      return `${Number(item.balance).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽`;
    },
    smsProviderDetail(key) {
      const item = this.smsProvider(key);
      if (!item) return 'нет данных';
      if (item.status === 'not_configured') return 'не настроен';
      if (item.status === 'ok') return 'API доступен · баланс обновляется раз в минуту';
      return item.error || 'API недоступен';
    },
    serviceStatus(value) {
      const normalized = String(value || '').toLowerCase();
      if (normalized === 'running' || normalized === 'active' || normalized === 'ok') return 'ok';
      if (!normalized || normalized === 'unknown') return 'unknown';
      return 'critical';
    },
    serviceValue(value) {
      const normalized = String(value || '').toLowerCase();
      if (normalized === 'running') return 'RUNNING';
      if (normalized === 'active') return 'ACTIVE';
      if (!normalized) return '—';
      return normalized.toUpperCase();
    },
    backupStatus(value) {
      const normalized = String(value || '').toLowerCase();
      if (normalized === 'ok') return 'ok';
      if (normalized === 'stale') return 'warning';
      if (normalized === 'error') return 'critical';
      return 'unknown';
    },
    backupValue(value) {
      const labels = {
        ok: 'OK',
        stale: 'STALE',
        error: 'ERROR',
        disabled: 'N/A',
        unknown: 'UNKNOWN',
      };
      return labels[String(value || '').toLowerCase()] || '—';
    },
    backupDetail(status, minutes) {
      if (String(status || '').toLowerCase() === 'disabled') return 'не контролируется в этом окружении';
      return this.ageLabel(minutes);
    },
    ageLabel(minutes) {
      const value = Number(minutes);
      if (minutes === null || minutes === undefined || Number.isNaN(value)) return 'возраст неизвестен';
      if (value < 60) return `${value} мин назад`;
      if (value < 1440) return `${Math.round(value / 60)} ч назад`;
      return `${Math.round(value / 144) / 10} дн назад`;
    },
    metricLabel(value, unit) {
      const parsed = Number(value);
      return Number.isNaN(parsed) ? '—' : `${Math.round(parsed * 10) / 10}${unit}`;
    },
    sparklinePoints(key, maxValue) {
      if (!this.metrics.length) return '';
      const width = 360;
      const height = key === 'apiResponseMs' ? 108 : 54;
      const denominator = Math.max(1, this.metrics.length - 1);
      return this.metrics.map((item, index) => {
        const numeric = Number(item[key]);
        const safe = Number.isNaN(numeric) ? 0 : Math.max(0, Math.min(maxValue, numeric));
        const x = (index / denominator) * width;
        const y = height - ((safe / maxValue) * height);
        return `${x.toFixed(2)},${y.toFixed(2)}`;
      }).join(' ');
    },
    async openIncident(id, updateRoute = true) {
      this.incidentModalOpen = true;
      this.incidentLoading = true;
      this.selectedIncident = null;
      if (updateRoute && String(this.$route.query.incident || '') !== String(id)) {
        this.$router.push({ query: { ...this.$route.query, incident: String(id) } });
      }
      try {
        const response = await monitoringIncident(id);
        this.selectedIncident = response.data;
      } finally {
        this.incidentLoading = false;
      }
    },
    closeIncident() {
      this.incidentModalOpen = false;
      this.selectedIncident = null;
      if (this.$route.query.incident) {
        const query = { ...this.$route.query };
        delete query.incident;
        this.$router.push({ query });
      }
    },
    async sendTestNotification() {
      if (this.testingNotification) return;
      this.testingNotification = true;
      this.testNotificationSent = false;
      try {
        await testMonitoringNotification();
        this.testNotificationSent = true;
        window.setTimeout(() => {
          this.testNotificationSent = false;
        }, 5000);
      } finally {
        this.testingNotification = false;
      }
    },
    async acknowledgeIncident() {
      if (!this.selectedIncident || this.acknowledging) return;
      this.acknowledging = true;
      try {
        const response = await acknowledgeMonitoringIncident(this.selectedIncident.id);
        this.selectedIncident = response.data;
        await this.fetchDashboard(true);
      } finally {
        this.acknowledging = false;
      }
    },
    statusLabel(status) {
      const labels = {
        ok: 'HEALTHY',
        warning: 'WARNING',
        critical: 'CRITICAL',
        unknown: 'UNKNOWN',
      };
      return labels[status] || 'UNKNOWN';
    },
    severityLabel(value) {
      const labels = { info: 'INFO', warning: 'WARNING', critical: 'CRITICAL' };
      return labels[value] || String(value || '').toUpperCase();
    },
    severityIcon(value) {
      if (value === 'critical') return '!';
      if (value === 'warning') return '!';
      return 'i';
    },
    incidentStatusLabel(value) {
      const labels = { open: 'Открыт', acknowledged: 'Просмотрен', resolved: 'Решён' };
      return labels[value] || value;
    },
    componentLabel(value) {
      const labels = {
        host: 'Сервер',
        disk: 'Диск',
        docker: 'Docker',
        mysql: 'MySQL',
        nginx: 'nginx',
        api: 'API',
        backup: 'Backup',
        laravel: 'Laravel',
        monitor: 'Watcher',
        ai: 'AI / REG.Cloud',
        notifications: 'Оповещения',
        system: 'Система',
      };
      return labels[value] || value || 'Система';
    },
    confidenceLabel(value) {
      const labels = { low: 'низкая', medium: 'средняя', high: 'высокая' };
      return labels[value] || value;
    },
    formatDate(value) {
      if (!value) return '—';
      return moment(value).format('DD.MM.YYYY HH:mm:ss');
    },
    formatRelative(value) {
      if (!value) return '—';
      return moment(value).fromNow();
    },
    truncate(value, length) {
      const text = String(value || '');
      return text.length > length ? `${text.slice(0, length)}…` : text;
    },
  },
};
</script>

<style lang="scss" scoped>
.monitoring-page {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: hidden;
  box-sizing: border-box;
  background: #f6f7f9;
  color: #20252b;
}

.monitoring-page *,
.monitoring-page *::before,
.monitoring-page *::after {
  box-sizing: border-box;
}

.monitoring-wrap {
  width: 100%;
  max-width: 1500px;
  min-width: 0;
  margin: 0 auto;
  padding: 28px 32px 56px;
  box-sizing: border-box;
}

.monitoring-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 20px;
}

.monitoring-title-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
}

.monitoring-title {
  margin: 0;
  font-size: 28px;
  line-height: 1.2;
  font-weight: 700;
}

.monitoring-subtitle {
  margin: 8px 0 0;
  color: #747c87;
  font-size: 14px;
}

.monitoring-header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.updated-at {
  font-size: 12px;
  color: #7b8490;
  white-space: nowrap;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 5px 9px;
  border-radius: 999px;
  font-size: 11px;
  line-height: 1;
  font-weight: 700;
  letter-spacing: .04em;
  background: #eef1f4;
  color: #67707a;
}

.status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
}

.status-ok { background: #eaf7ef; color: #21824a; }
.status-warning { background: #fff6df; color: #a66b00; }
.status-critical { background: #fff0f0; color: #c83f49; }
.status-unknown { background: #eef1f4; color: #67707a; }

.stale-banner {
  margin-bottom: 18px;
  padding: 13px 16px;
  border: 1px solid #f0c4c8;
  border-radius: 10px;
  background: #fff4f4;
  color: #a7333c;
  font-size: 13px;
}

.range-row {
  display: flex;
  gap: 6px;
  margin-bottom: 20px;
}

.range-btn,
.monitor-btn {
  border: 0;
  cursor: pointer;
  transition: 120ms ease;
}

.range-btn {
  padding: 7px 12px;
  border-radius: 8px;
  background: #fff;
  color: #69727d;
  font-size: 12px;
  box-shadow: 0 0 0 1px #e7eaee inset;
}

.range-btn:hover,
.range-btn.active {
  color: #242a31;
  background: #eceff3;
}

.monitor-btn {
  padding: 9px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
}

.monitor-btn:disabled { opacity: .55; cursor: default; }
.monitor-btn-primary { background: #242a31; color: #fff; }
.monitor-btn-secondary { background: #fff; color: #353b43; box-shadow: 0 0 0 1px #dfe3e8 inset; }

.health-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 12px;
}

::v-deep .health-card,
.metric-card,
.panel {
  background: #fff;
  border: 1px solid #e7eaee;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(28, 36, 45, .03);
}

::v-deep .health-card { padding: 16px; min-height: 112px; }
::v-deep .health-card-head { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
::v-deep .health-card-title { color: #737c87; font-size: 12px; font-weight: 600; }
::v-deep .health-indicator { width: 9px; height: 9px; border-radius: 50%; background: #9ca4ad; box-shadow: 0 0 0 4px #f0f2f4; }
::v-deep .indicator-ok { background: #31a566; box-shadow: 0 0 0 4px #eaf7ef; }
::v-deep .indicator-warning { background: #d99a24; box-shadow: 0 0 0 4px #fff6df; }
::v-deep .indicator-critical { background: #d64d58; box-shadow: 0 0 0 4px #fff0f0; }
::v-deep .health-card-value { margin-top: 15px; font-size: 20px; line-height: 1.1; font-weight: 700; }
::v-deep .health-card-detail { margin-top: 7px; color: #8a929c; font-size: 11px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.resource-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 12px;
}

.metric-card { padding: 14px 16px; }
::v-deep .metric-card-top { display: flex; justify-content: space-between; gap: 10px; color: #6f7883; font-size: 12px; }
::v-deep .metric-value { color: #252b32; font-size: 14px; }
::v-deep .metric-warning { color: #a66b00; }
::v-deep .metric-critical { color: #c83f49; }
::v-deep .metric-unknown { color: #8f98a2; }
::v-deep .metric-track { height: 5px; margin-top: 12px; overflow: hidden; border-radius: 999px; background: #edf0f3; }
::v-deep .metric-fill { height: 100%; border-radius: inherit; background: #65717d; }
::v-deep .metric-fill-warning { background: #d99a24; }
::v-deep .metric-fill-critical { background: #d64d58; }
::v-deep .metric-fill-unknown { background: #c4c9cf; }

.charts-grid,
.two-column-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.45fr) minmax(320px, .75fr);
  gap: 12px;
  margin-bottom: 12px;
}

.panel { overflow: hidden; }
.panel-heading { display: flex; justify-content: space-between; gap: 18px; padding: 17px 18px 14px; border-bottom: 1px solid #edf0f3; }
.panel-heading h2 { margin: 0; font-size: 15px; font-weight: 700; }
.panel-heading p { margin: 5px 0 0; color: #8a929c; font-size: 11px; }
.chart-panel { min-height: 255px; }
.chart-row { display: grid; grid-template-columns: 94px 1fr; align-items: center; gap: 12px; padding: 11px 18px; }
.chart-label { display: flex; flex-direction: column; gap: 3px; color: #747d87; font-size: 11px; }
.chart-label strong { color: #272d34; font-size: 14px; }
.sparkline { width: 100%; height: 56px; overflow: visible; }
.sparkline-large { height: 110px; padding: 0 18px; box-sizing: border-box; }
.chart-line { fill: none; stroke: #5d6a76; stroke-width: 2; vector-effect: non-scaling-stroke; }
.chart-base { stroke: #eef0f3; stroke-width: 1; vector-effect: non-scaling-stroke; }
.api-chart-value { padding: 22px 18px 4px; font-size: 27px; font-weight: 700; }
.chart-footnote { padding: 4px 18px 16px; color: #939aa3; font-size: 10px; }

.incidents-panel { margin-bottom: 12px; }
.incident-heading { align-items: center; }
.incident-counters { display: flex; gap: 7px; }
.counter { padding: 4px 7px; border-radius: 6px; font-size: 10px; font-weight: 700; }
.counter.critical { background: #fff0f0; color: #c83f49; }
.counter.warning { background: #fff6df; color: #9a670a; }
.all-clear { display: flex; align-items: center; gap: 13px; padding: 22px 18px; }
.all-clear-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #eaf7ef;
  color: #21824a;
  font-weight: 800;
}
.all-clear strong { display: block; font-size: 13px; }
.all-clear p { margin: 4px 0 0; color: #89919b; font-size: 11px; }
.incident-list { display: flex; flex-direction: column; }
.incident-row {
  width: 100%;
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr) auto 18px;
  align-items: center;
  gap: 11px;
  padding: 14px 18px;
  border: 0;
  border-bottom: 1px solid #f0f2f4;
  background: #fff;
  text-align: left;
  cursor: pointer;
}
.incident-row:last-child { border-bottom: 0; }
.incident-row:hover { background: #fafbfc; }
.incident-severity { display: inline-flex; align-items: center; justify-content: center; width: 25px; height: 25px; border-radius: 50%; font-weight: 800; font-size: 12px; }
.incident-severity.critical { background: #fff0f0; color: #c83f49; }
.incident-severity.warning { background: #fff6df; color: #a66b00; }
.incident-severity.info { background: #edf3fb; color: #4778a9; }
.incident-main { min-width: 0; display: flex; flex-direction: column; gap: 4px; }
.incident-title { font-size: 13px; font-weight: 650; color: #242a31; }
.incident-meta { color: #8b939d; font-size: 10px; }
.incident-ai { color: #626c77; font-size: 11px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.incident-state { display: inline-flex; padding: 4px 7px; border-radius: 6px; font-size: 10px; white-space: nowrap; background: #f0f2f4; color: #6c7580; }
.incident-state.open { background: #fff0f0; color: #b43d47; }
.incident-state.acknowledged { background: #fff6df; color: #95650b; }
.incident-state.resolved { background: #eaf7ef; color: #237d4a; }
.incident-arrow { color: #adb3ba; font-size: 22px; }

.events-list,
.history-list { display: flex; flex-direction: column; }
.event-row { display: grid; grid-template-columns: 9px 1fr; gap: 10px; padding: 11px 18px; border-bottom: 1px solid #f1f3f5; }
.event-row:last-child { border-bottom: 0; }
.event-dot { width: 7px; height: 7px; margin-top: 4px; border-radius: 50%; background: #9da5ae; }
.event-dot.critical { background: #d64d58; }
.event-dot.warning { background: #d99a24; }
.event-dot.info { background: #5784b0; }
.event-dot.ok { background: #31a566; }
.event-content { display: flex; min-width: 0; flex-direction: column; gap: 3px; }
.event-content strong { font-size: 11px; font-weight: 600; }
.event-content span { color: #9299a2; font-size: 10px; }
.history-row {
  width: 100%;
  display: grid;
  grid-template-columns: 9px 1fr auto;
  align-items: center;
  gap: 10px;
  padding: 11px 18px;
  border: 0;
  border-bottom: 1px solid #f1f3f5;
  background: #fff;
  text-align: left;
  cursor: pointer;
}
.history-row:hover { background: #fafbfc; }
.history-main { display: flex; min-width: 0; flex-direction: column; gap: 3px; }
.history-main strong { overflow: hidden; color: #30363d; font-size: 11px; font-weight: 600; white-space: nowrap; text-overflow: ellipsis; }
.history-main span { color: #9299a2; font-size: 10px; }
.panel-empty { padding: 22px 18px; color: #8b939d; font-size: 12px; }

.empty-state { margin-bottom: 14px; padding: 28px; border: 1px dashed #cfd5db; border-radius: 12px; background: #fff; }
.empty-state-title { font-weight: 700; font-size: 14px; }
.empty-state-text { max-width: 720px; margin-top: 7px; color: #7d8690; font-size: 12px; line-height: 1.6; }

.incident-modal-backdrop { position: fixed; z-index: 9999; inset: 0; display: flex; justify-content: flex-end; background: rgba(26, 31, 37, .36); }
.incident-modal {
  width: 92vw;
  max-width: 760px;
  height: 100%;
  overflow-y: auto;
  background: #f7f8fa;
  box-shadow: -12px 0 40px rgba(24, 31, 38, .15);
}
.incident-modal-header {
  position: sticky;
  z-index: 2;
  top: 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding: 21px 24px;
  border-bottom: 1px solid #e2e6ea;
  background: rgba(255, 255, 255, .98);
}
.modal-kicker { margin-bottom: 5px; color: #8b939d; font-size: 10px; text-transform: uppercase; letter-spacing: .05em; }
.incident-modal-header h2 { margin: 0; font-size: 19px; line-height: 1.35; }
.modal-close { padding: 0 4px; border: 0; background: transparent; color: #7e8791; font-size: 30px; line-height: 1; cursor: pointer; }
.modal-loading { color: #747d87; font-size: 13px; }
.incident-modal-body { padding: 18px 24px 90px; }
.incident-summary-strip { display: flex; align-items: center; flex-wrap: wrap; gap: 8px 12px; margin-bottom: 14px; color: #7f8791; font-size: 10px; }
.detail-section { margin-bottom: 12px; padding: 17px 18px; border: 1px solid #e4e8ec; border-radius: 10px; background: #fff; }
.ai-section { border-color: #d9e4ef; background: #fbfdff; }
.detail-section-title { margin-bottom: 10px; font-size: 11px; font-weight: 750; text-transform: uppercase; letter-spacing: .04em; color: #656f79; }
.detail-text { margin: 0; font-size: 13px; line-height: 1.65; white-space: pre-wrap; }
.ai-confidence { margin-top: 10px; color: #7d8691; font-size: 10px; }
.recommendations,
.technical-text { margin: 0; overflow: visible; color: #30373e; font-family: inherit; font-size: 12px; line-height: 1.65; white-space: pre-wrap; }
.logs-box {
  max-height: 420px;
  margin: 0;
  overflow: auto;
  padding: 14px;
  border-radius: 8px;
  background: #20252b;
  color: #d8dee5;
  font-family: Consolas, Monaco, monospace;
  font-size: 10px;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
}
.incident-events { margin: 0 -18px -17px; }
.incident-modal-footer {
  position: fixed;
  right: 0;
  bottom: 0;
  width: 92vw;
  max-width: 760px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 9px;
  padding: 13px 24px;
  border-top: 1px solid #e2e6ea;
  background: rgba(255, 255, 255, .98);
  box-sizing: border-box;
}
.footer-note { margin-right: auto; color: #8b939d; font-size: 10px; }

@media (max-width: 1200px) {
  .health-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .resource-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 900px) {
  .monitoring-wrap { padding: 20px 16px 48px; }
  .monitoring-header { flex-direction: column; }
  .charts-grid,
  .two-column-grid { grid-template-columns: 1fr; }
  .monitoring-header-actions { width: 100%; justify-content: space-between; }
}

@media (max-width: 620px) {
  .health-grid,
  .resource-grid { grid-template-columns: 1fr; }
  .incident-row { grid-template-columns: 28px minmax(0, 1fr) 18px; }
  .incident-row .incident-state { display: none; }
  .footer-note { display: none; }
}

.ai-settings-panel { margin-bottom: 20px; }
.ai-settings-heading { align-items: center; }
.ai-settings-loading { padding: 20px 18px; color: #8a929c; font-size: 12px; }
.ai-settings-body { padding: 18px; }
.ai-settings-main-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  align-items: center;
  margin-bottom: 16px;
}
.setting-toggle,
.ai-limit-field {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  padding: 0 11px;
  border: 1px solid #e5e8ec;
  border-radius: 9px;
  background: #fafbfc;
  color: #414850;
  font-size: 12px;
}
.setting-toggle-master { font-weight: 700; }
.setting-toggle input { margin: 0; }
.ai-limit-field { grid-column: span 2; justify-content: space-between; }
.ai-limit-field input { width: 88px; padding: 7px 8px; border: 1px solid #dfe3e8; border-radius: 7px; background: #fff; }
.provider-table { border: 1px solid #e8ebef; border-radius: 10px; overflow: hidden; }
.provider-table-head,
.provider-row {
  display: grid;
  grid-template-columns: 44px minmax(135px, .65fr) minmax(220px, 1.6fr) 72px;
  gap: 9px;
  align-items: center;
  padding: 9px 12px;
}
.provider-table-head { background: #f5f7f9; color: #858d96; font-size: 10px; font-weight: 700; text-transform: uppercase; }
.provider-row { border-top: 1px solid #edf0f3; }
.provider-row select,
.provider-row input[type="text"] {
  width: 100%;
  box-sizing: border-box;
  padding: 8px 9px;
  border: 1px solid #dfe3e8;
  border-radius: 7px;
  background: #fff;
  color: #353b43;
  font-size: 12px;
}
.provider-mode { display: inline-flex; justify-content: center; padding: 4px 6px; border-radius: 6px; font-size: 9px; font-weight: 800; }
.provider-mode.free { background: #eaf7ef; color: #21824a; }
.provider-mode.paid { background: #fff6df; color: #9a670a; }
.ai-settings-note { margin-top: 12px; color: #7e8791; font-size: 11px; line-height: 1.55; }
.ai-settings-actions { display: flex; align-items: center; gap: 12px; margin-top: 14px; }
.ai-usage-hint { color: #7e8791; font-size: 11px; }
.diagnostic-export { display: flex; justify-content: space-between; gap: 18px; align-items: center; margin-top: 18px; padding-top: 16px; border-top: 1px solid #edf0f3; }
.diagnostic-export > div:first-child { display: flex; flex-direction: column; gap: 4px; }
.diagnostic-export strong { font-size: 12px; }
.diagnostic-export span { color: #8a929c; font-size: 10px; }
.diagnostic-actions { display: flex; flex-wrap: wrap; gap: 6px; justify-content: flex-end; }

@media (max-width: 1100px) {
  .ai-settings-main-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .diagnostic-export { align-items: flex-start; flex-direction: column; }
  .diagnostic-actions { justify-content: flex-start; }
}

@media (max-width: 760px) {
  .ai-settings-main-grid { grid-template-columns: 1fr; }
  .ai-limit-field { grid-column: span 1; }
  .provider-table {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    overflow: hidden;
  }
  .provider-table-head { display: none; }
  .provider-row {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    grid-template-columns: 28px minmax(0, 1fr) auto;
    gap: 7px;
    padding: 10px;
  }
  .provider-row > input[type="checkbox"] {
    grid-column: 1;
    grid-row: 1;
    align-self: center;
    justify-self: center;
    margin: 0;
  }
  .provider-row > select {
    grid-column: 2;
    grid-row: 1;
    min-width: 0;
  }
  .provider-row > input[type="text"] {
    grid-column: 1 / -1;
    grid-row: 2;
    min-width: 0;
  }
  .provider-row > .provider-mode {
    grid-column: 3;
    grid-row: 1;
    align-self: center;
  }
}
.ai-settings-preset-note { margin-bottom: 14px; padding: 10px 12px; border-radius: 8px; background: #eef3f8; color: #536170; font-size: 11px; line-height: 1.45; }

@media (max-width: 767px) {
  .monitoring-page {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    overflow-x: hidden;
  }
  .monitoring-wrap {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    padding: 10px 8px 24px;
    box-sizing: border-box;
    overflow-x: hidden;
  }
  .monitoring-header > div,
  .monitoring-title-row,
  .monitoring-header-actions,
  .panel,
  .ai-settings-body,
  .ai-settings-main-grid,
  .provider-table,
  .diagnostic-export,
  .diagnostic-actions,
  .health-grid,
  .resource-grid,
  .charts-grid,
  .two-column-grid,
  .chart-panel,
  .chart-row  {
    min-width: 0;
    max-width: 100%;
  }
  .monitoring-title {
    font-size: 21px;
  }
  .monitoring-subtitle {
    max-width: 100%;
    margin-top: 5px;
    font-size: 12px;
    line-height: 1.45;
    overflow-wrap: anywhere;
  }
  .monitoring-header-actions {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    justify-content: stretch;
    gap: 6px;
  }
  .monitoring-header-actions .updated-at {
    grid-column: 1 / -1;
    white-space: normal;
  }
  .monitoring-header-actions .monitor-btn {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    min-height: 36px;
    padding: 7px 8px;
    white-space: normal;
    overflow-wrap: anywhere;
  }
  .range-row {
    gap: 5px;
    margin: 0 -8px 12px;
    padding: 0 8px 2px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
  }
  .range-row::-webkit-scrollbar {
    display: none;
  }
  .range-btn {
    flex: 0 0 auto;
    min-height: 36px;
  }
  .panel-heading {
    min-width: 0;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 8px;
    padding: 11px 12px;
  }
  .panel-heading > div {
    min-width: 0;
    max-width: 100%;
  }
  .panel-heading p,
  .ai-settings-preset-note,
  .ai-settings-note,
  .diagnostic-export strong,
  .diagnostic-export span {
    max-width: 100%;
    overflow-wrap: anywhere;
  }
  .ai-settings-body {
    padding: 10px;
  }
  .ai-settings-main-grid {
    gap: 7px;
    margin-bottom: 10px;
  }
  .setting-toggle,
  .ai-limit-field {
    min-width: 0;
    min-height: 38px;
    padding: 7px 9px;
  }
  .ai-limit-field {
    flex-wrap: wrap;
  }
  .ai-limit-field input,
  .provider-row select,
  .provider-row input[type="text"] {
    max-width: 100%;
    min-width: 0;
    min-height: 38px;
    font-size: 16px;
  }
  .ai-settings-actions {
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 7px;
    margin-top: 10px;
  }
  .ai-settings-actions .monitor-btn {
    min-height: 36px;
  }
  .ai-usage-hint {
    flex: 1 1 100%;
    line-height: 1.4;
  }
  .diagnostic-export {
    gap: 10px;
    margin-top: 12px;
    padding-top: 12px;
  }
  .diagnostic-actions {
    width: 100%;
    max-width: 100%;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 6px;
  }
  .diagnostic-actions .monitor-btn {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    min-height: 36px;
    padding-left: 8px;
    padding-right: 8px;
    white-space: normal;
    overflow-wrap: anywhere;
  }
  .diagnostic-actions .monitor-btn:last-child {
    grid-column: 1 / -1;
  }
  .chart-row {
    grid-template-columns: 72px minmax(0, 1fr);
    gap: 8px;
    padding: 9px 12px;
  }
  .sparkline,
  .sparkline-large {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    overflow: hidden;
  }
  .sparkline-large {
    padding-left: 12px;
    padding-right: 12px;
  }
  .incident-modal {
    width: 100vw;
    max-width: none;
  }
  .incident-modal-header {
    gap: 10px;
    padding: calc(12px + env(safe-area-inset-top)) 12px 12px;
  }
  .incident-modal-header h2 {
    font-size: 17px;
  }
  .modal-close {
    flex: 0 0 38px;
    width: 38px;
    min-height: 38px;
  }
  .incident-modal-body {
    padding: 12px 10px calc(82px + env(safe-area-inset-bottom));
  }
  .detail-section {
    padding: 12px;
  }
  .incident-events {
    margin: 0 -12px -12px;
  }
  .incident-modal-footer {
    width: 100vw;
    max-width: none;
    flex-wrap: wrap;
    gap: 6px;
    padding: 9px 10px calc(9px + env(safe-area-inset-bottom));
  }
  .incident-modal-footer .monitor-btn {
    min-height: 36px;
  }
}
@media (max-width: 420px) {
  .monitoring-title-row {
    gap: 7px;
  }
  .incident-modal-footer .monitor-btn {
    flex: 1 1 100%;
    width: 100%;
  }
}
</style>
